import React, { useEffect, useState } from "react";

import {
  IconButton,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
} from "@mui/material";
import Selector from "./Selector";
import AutoCompleteDialog from "./AutoCompleteDialog";
import { COLORS } from "../constants/Colors";

const RenderTableElements = (props) => {
  switch (props.type) {
    case "textfield":
      return (
        <TextField
          margin="dense"
          value={props.value}
          onChange={props.onChange}
          onBlur={props.onBlur}
          aria-label={props.label}
          placeholder={props.placeholder}
          error={props.error}
          sx={{
            width: "100% !important",
            margin: "0px",
            maxHeight: "50px",
            minHeight: "50px",
            padding: "0px !important",
            "& input": {
              border: "none",
              maxWidth: props.maxWidth || "300px",
              minWidth: props.minWidth || "300px",
              padding: "0px",
            },
            "& .MuiOutlinedInput-root": {
              padding: "0px !important",
              margin: "0px !important",
              height: "20px",
            },
            ...props.customStyle,
          }}
        />
      );
    case "textarea":
      return (
        <TextField
          margin="dense"
          value={props.value}
          onChange={props.onChange}
          onBlur={props.onBlur}
          aria-label={props.label}
          placeholder={props.placeholder}
          error={props.error}
          multiline
          rows={props.rows || 3}
          sx={{
            width: "100% !important",
            margin: "0px",
            padding: "0px !important",
            "& textarea": {
              border: "none",
              maxWidth: props.maxWidth || "400px",
              minWidth: props.minWidth || "300px",
              padding: "10px !important",
              lineHeight: "1.5em",
              resize: "none",
              overflow: props.rows > 3 ? "auto" : "hidden",
              minHeight: "72px", // 3 rows * 24px line height
              maxHeight: props.rows > 3 ? `${props.rows * 24}px` : "72px",
            },
            "& .MuiInputBase-root": {
              height: "auto",
            },
            "& .MuiOutlinedInput-root": {
              padding: "0px !important",
              margin: "0px !important",
              height: "auto",
            },
            ...props.customStyle,
          }}
        />
      );
    case "selector":
      return (
        <Selector
          items={props.items}
          error={props.error}
          customStyles={{
            height: "20px",
            width: "150px",
            minWidth: props.minWidth || "120px",
            zIndex: props.zIndex,
            marginTop: "15px",
            "& input": {
              border: "none",
              width: "300px",
              padding: "0px",
            },
            "& .MuiSelect-select": {
              paddingLeft: "0px",
            },
            "& .MuiOutlinedInput-root": {
              padding: "0px",
              height: "20px",
            },
            "& .MuiPaper-root": {
              zIndex: props.zIndex + " !important",
            },
          }}
          defaultValue={props.value}
          setValue={props.setValue}
          onBlur={props.onBlur}
        />
      );
    case "tags":
      return (
        <AutoCompleteDialog
          value={props.value}
          title={props.title}
          label={props.label}
          infoText={props.infoText}
          saveButtonClick={props.saveButtonClick}
          cancelButtonClick={props.cancelButtonClick}
          options={props.options}
        />
      );
    case "icon":
      return (
        <IconButton
          onClick={props.onClick}
          size="small"
          aria-label={props.title || "icon"}
        >
          {props.icon}
        </IconButton>
      );
  }
  return (
    <div className="tableDivDefaultElement" style={{ ...props.customStyle }}>
      {props.value}
    </div>
  );
};

export default function Table({
  tableContents = [], // two dimensional array containing rows and columns
  rowHeaders = [],
  customStyle = {},
  customTableStyle = {},
  rowsPerPageProp = 10,
  rowCount = null,
  rowsPerPageOptions = [10, 20, 50, 100],
  pagination = false,
  onChangeRowsPerPage,
  onChangePage,
  serverSidePagination = false,
  pageNumber = 0,
  emptyText = "Nothing To Display",
  currentOrderBy,
  currentOrder,
  onRequestSort,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageProp);

  const count = rowCount ? rowCount : tableContents.length;

  useEffect(() => {
    setPage(pageNumber);
  }, [pageNumber]);

  const handleChangePage = (event, newPage) => {
    if (onChangePage) {
      onChangePage(newPage);
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    if (onChangeRowsPerPage) {
      onChangeRowsPerPage(parseInt(event.target.value, 10));
    }
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedTable = (contents) => {
    if (pagination && !serverSidePagination) {
      return contents.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      );
    }

    return contents;
  };

  return (
    <TableContainer
      sx={{
        ...customStyle,
        width: "100%",
        marginBottom: "50px",
      }}
    >
      <MuiTable style={{ ...customTableStyle }} stickyHeader>
        <TableHead>
          <TableRow>
            {rowHeaders.length > 0 &&
              rowHeaders
                .filter((el) => el.columnData != null)
                .map((header) => {
                  // Default to true
                  const isSortable = header.sortable !== false;
                  return (
                    <TableCell
                      align="left"
                      key={`table-header-${header.id}`}
                      sx={header?.style}
                      sortDirection={
                        currentOrderBy === header.id ? currentOrder : false
                      }
                    >
                      {isSortable && onRequestSort ? (
                        <TableSortLabel
                          active={currentOrderBy === header.id}
                          direction={
                            currentOrderBy === header.id ? currentOrder : "asc"
                          }
                          onClick={() => onRequestSort(header.id)}
                        >
                          {header.columnData}
                        </TableSortLabel>
                      ) : (
                        header.columnData
                      )}
                    </TableCell>
                  );
                })}
          </TableRow>
        </TableHead>
        <TableBody>
          {tableContents.length > 0 ? (
            paginatedTable(tableContents).map((row, rowIndex) => (
              <TableRow
                hover
                key={`table-row-${rowIndex}`}
                sx={{ "& td": { transition: "all 0.3s ease" } }}
              >
                {row
                  .filter((col) => col.element != null)
                  .map((col) => (
                    <TableCell
                      align="left"
                      key={`table-col-${col.id}`}
                      sx={{
                        height:
                          col?.element?.type === "textarea"
                            ? `${(col?.element?.rows || 3) * 24}px`
                            : "65px",
                        transition: "all 0.3s ease",
                        paddingTop: "16px", // Add top padding for better alignment
                        wordBreak: "break-word",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        ...(col?.style ? col.style : {}),
                      }}
                    >
                      {col.element ? (
                        <RenderTableElements key={col.id} {...col.element} />
                      ) : (
                        col.columnData
                      )}
                    </TableCell>
                  ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                sx={{ textAlign: "center", height: "66px" }}
                colSpan={rowHeaders.length}
              >
                {emptyText}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </MuiTable>

      {pagination && (
        <TablePagination
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          count={count}
          page={page}
          component="div"
          onPageChange={handleChangePage}
          onRowsPerPageChange={(event) => handleChangeRowsPerPage(event)}
          slotProps={{
            actions: {
              previousButton: {
                sx: {
                  backgroundColor: COLORS.PRIMARY_COLOR_3,
                  borderRadius: "10px",
                  color: "#fff",
                  padding: "10px",
                  ":hover": {
                    backgroundColor: "#2966C7",
                  },
                  ":disabled": {
                    backgroundColor: "#d0e1fd",
                  },
                },
              },
              nextButton: {
                sx: {
                  backgroundColor: COLORS.PRIMARY_COLOR_3,
                  borderRadius: "10px",
                  marginLeft: "10px",
                  padding: "10px",
                  color: "#fff",
                  ":hover": {
                    backgroundColor: "#2966C7",
                  },
                  ":disabled": {
                    backgroundColor: "#d0e1fd",
                  },
                },
              },
            },
          }}
          sx={{
            ".MuiTablePagination-toolbar": {
              padding: "0",
              justifyContent: "flex-end",
            },
            ".MuiTablePagination-spacer": {
              display: "none",
            },
            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
              {
                padding: "0",
              },
            ".MuiTablePagination-displayedRows": {
              border: "1px solid #e0e0e0",
              borderRadius: "10px",
              padding: "5px 15px",
            },
            ".MuiTablePagination-selectLabel": {
              display: "none",
            },
            ".MuiTablePagination-input": {
              display: "none",
            },
          }}
        />
      )}
    </TableContainer>
  );
}
